import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Save,
    Loader2,
    Instagram,
    Facebook,
    Linkedin,
    Share2,
    Music2,
    Trash2,
    Megaphone,
    ShieldCheck,
    History,
    UserX,
    Search,
    UserCheck,
    TrendingDown,
    Image as ImageIcon,
    CheckCircle2,
    XCircle,
    ExternalLink,
} from "lucide-react";
import api from "../../lib/api";
import { useConfirm } from "../../components/ConfirmDialog";
import ThemedSelect from "../../components/ThemedSelect";
import TrainingReminderSection from "./TrainingReminderSection";

const LINK_FIELDS = [
    {
        key: "instagram_url",
        label: "Instagram",
        placeholder: "https://www.instagram.com/shariti.officiel",
        Icon: Instagram,
    },
    {
        key: "facebook_url",
        label: "Facebook",
        placeholder: "https://www.facebook.com/Shariti",
        Icon: Facebook,
    },
    {
        key: "linkedin_url",
        label: "LinkedIn",
        placeholder: "https://www.linkedin.com/company/shariti",
        Icon: Linkedin,
    },
    {
        key: "tiktok_url",
        label: "TikTok",
        placeholder: "https://www.tiktok.com/@shariti",
        Icon: Music2,
    },
    {
        key: "share_post_url",
        label: "Lien post à partager",
        placeholder: "URL d'un post Shariti à partager (ex: dernier post Insta)",
        Icon: Share2,
    },
];

function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export default function AdminEngagement() {
    const confirm = useConfirm();
    const [links, setLinks] = useState(null);
    const [saving, setSaving] = useState(false);
    const [completions, setCompletions] = useState([]);
    const [tab, setTab] = useState("active"); // active | invalidated
    const [filterUserId, setFilterUserId] = useState(""); // "" = all
    const [stats, setStats] = useState(null);

    const reload = async () => {
        const params = { status: tab };
        if (filterUserId) params.user_id = filterUserId;
        const [l, c, s] = await Promise.all([
            api.get("/admin/social-settings"),
            api.get("/admin/social-completions", { params }),
            api.get("/admin/social-completions/stats"),
        ]);
        setLinks(l.data);
        setCompletions(c.data);
        setStats(s.data);
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, filterUserId]);

    const userOptions = useMemo(() => {
        const map = new Map();
        completions.forEach((c) => {
            if (!map.has(c.user_id)) {
                map.set(c.user_id, {
                    value: c.user_id,
                    label: `${c.user_name} · ${c.user_email}`,
                });
            }
        });
        return [{ value: "", label: "Tous les recruteurs" }, ...map.values()];
    }, [completions]);

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/social-settings", links);
            toast.success("Liens enregistrés — visibles dès la prochaine vue Profil");
            await reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    const invalidate = async (row) => {
        const ok = await confirm({
            title: `Invalider cette validation ?`,
            description: `${row.user_name} perdra les ${row.points_awarded} pts attribués pour « ${row.action_label} ». L'opération sera tracée (ton nom + horodatage) et sera consultable dans l'onglet « Historique ».`,
            confirmLabel: "Invalider et retirer les points",
            tone: "danger",
        });
        if (!ok) return;
        try {
            const cid = encodeURIComponent(row.completion_id);
            const r = await api.post(`/admin/social-completions/${cid}/invalidate`);
            toast.success(
                `Validation retirée • -${r.data?.points_revoked ?? row.points_awarded} pts • Tracé`,
            );
            await reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de l'invalidation");
        }
    };

    // iter-23 — approve a pending proof (awards points)
    const approveProof = async (row) => {
        const ok = await confirm({
            title: "Valider cette preuve ?",
            description: `${row.user_name} recevra ses points pour « ${row.action_label} ».`,
            confirmLabel: "Valider et créditer les points",
        });
        if (!ok) return;
        try {
            const cid = encodeURIComponent(row.completion_id);
            const r = await api.post(`/admin/social-completions/${cid}/approve`);
            toast.success(`Preuve validée — +${r.data?.points_awarded || 0} pts`);
            await reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    };

    const rejectProof = async (row) => {
        const reason = window.prompt(
            `Rejeter la preuve de ${row.user_name} pour « ${row.action_label} » ?\nRaison (communiquée au recruteur) :`,
            "",
        );
        if (reason === null) return;
        try {
            const cid = encodeURIComponent(row.completion_id);
            await api.post(`/admin/social-completions/${cid}/reject`, {
                reason: reason || null,
            });
            toast.success("Preuve refusée");
            await reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    };

    const buildProofUrl = (proofUrl) => {
        if (!proofUrl) return "";
        if (proofUrl.startsWith("http") || proofUrl.startsWith("data:")) return proofUrl;
        // proof_url starts with /api/files/... or /uploads/... — prefix the backend host
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        return `${base}${proofUrl}`;
    };

    if (!links || !stats) {
        return <p className="text-white/40 italic text-sm">Chargement...</p>;
    }

    return (
        <div className="space-y-5" data-testid="admin-engagement">
            {/* URLs configuration */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-3xl p-5 lg:p-6"
            >
                <div className="flex items-start gap-3 mb-5">
                    <div className="w-11 h-11 rounded-2xl bg-shariti-orange/15 flex items-center justify-center shrink-0">
                        <Megaphone className="w-5 h-5 text-shariti-orange" />
                    </div>
                    <div>
                        <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-1">
                            Réseaux Shariti
                        </p>
                        <h3 className="text-xl font-extrabold text-white tracking-tight">
                            Liens des comptes officiels
                        </h3>
                        <p className="text-xs text-white/55 font-light mt-1 max-w-2xl">
                            Configure les URLs vers lesquelles les recruteurs sont redirigés pour valider leurs actions de promotion. Tant qu'un lien est vide, l'action correspondante reste désactivée côté apprenant.
                        </p>
                    </div>
                </div>

                <div className="space-y-3">
                    {LINK_FIELDS.map(({ key, label, placeholder, Icon }) => (
                        <div key={key}>
                            <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block flex items-center gap-1.5">
                                <Icon className="w-3.5 h-3.5" /> {label}
                            </label>
                            <input
                                type="url"
                                placeholder={placeholder}
                                value={links[key] || ""}
                                onChange={(e) =>
                                    setLinks({ ...links, [key]: e.target.value })
                                }
                                data-testid={`social-link-${key}`}
                                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/40 text-sm"
                            />
                        </div>
                    ))}
                </div>

                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid="social-links-save"
                    className="w-full mt-5 bg-shariti-orange text-white font-extrabold py-3 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Enregistrer les liens
                </button>
            </motion.div>

            {/* Iter-35 — Relance formation par email */}
            <TrainingReminderSection />

            {/* KPIs strip */}
            <div className="grid grid-cols-3 gap-3" data-testid="engagement-kpis">
                <div className="glass-card rounded-2xl p-4">
                    <p className="text-[10px] uppercase tracking-widest text-white/45 font-thin mb-1">
                        Validations actives
                    </p>
                    <p className="text-2xl font-extrabold text-white tabular-nums">
                        {stats.active_count}
                    </p>
                </div>
                <div className="glass-card rounded-2xl p-4">
                    <p className="text-[10px] uppercase tracking-widest text-white/45 font-thin mb-1">
                        Invalidations ce mois
                    </p>
                    <p className="text-2xl font-extrabold text-shariti-yellow tabular-nums">
                        {stats.invalidations_this_month}
                    </p>
                </div>
                <div className="glass-card rounded-2xl p-4 flex items-start justify-between gap-2">
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-white/45 font-thin mb-1">
                            Points retirés ce mois
                        </p>
                        <p className="text-2xl font-extrabold text-red-400 tabular-nums">
                            -{stats.points_revoked_month}
                        </p>
                    </div>
                    <TrendingDown className="w-4 h-4 text-red-400/60 shrink-0 mt-1" />
                </div>
            </div>

            {/* Completion validation list */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="glass-card rounded-3xl p-5 lg:p-6"
            >
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-11 h-11 rounded-2xl bg-shariti-yellow/15 flex items-center justify-center shrink-0">
                        <ShieldCheck className="w-5 h-5 text-shariti-yellow" />
                    </div>
                    <div className="flex-1">
                        <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-yellow mb-1">
                            Modération engagement
                        </p>
                        <h3 className="text-xl font-extrabold text-white tracking-tight">
                            {tab === "active"
                                ? `Validations actives (${stats.active_count})`
                                : tab === "pending_review"
                                  ? `Preuves à valider (${completions.length})`
                                  : `Historique des invalidations (${stats.invalidated_total})`}
                        </h3>
                        <p className="text-xs text-white/55 font-light mt-1">
                            {tab === "active"
                                ? "Si tu repères une déclaration suspecte, tu peux invalider et retirer les points. L'action est tracée."
                                : tab === "pending_review"
                                  ? "Les recruteurs ont fait l'action et envoyé leur capture. Valide ou refuse leur preuve."
                                  : "Toutes les invalidations passées avec qui les a effectuées et quand."}
                        </p>
                    </div>
                </div>

                {/* Sub-tabs + filter */}
                <div className="flex flex-col sm:flex-row gap-2 mb-4">
                    <div
                        className="overflow-x-auto scrollbar-hide -mx-1 px-1"
                        data-testid="completion-tabs"
                    >
                        <div className="flex gap-1 p-1 glass-card rounded-full w-max text-xs">
                            {[
                                { id: "active", label: "Actives", icon: ShieldCheck },
                                { id: "pending_review", label: "Preuves à valider", icon: ImageIcon },
                                { id: "invalidated", label: "Historique", icon: History },
                            ].map((t) => (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setTab(t.id)}
                                    data-testid={`completion-tab-${t.id}`}
                                    className={`px-3 py-1.5 rounded-full font-bold transition-all flex items-center gap-1.5 whitespace-nowrap ${
                                        tab === t.id
                                            ? "bg-shariti-orange text-white"
                                            : "text-white/60 hover:text-white"
                                    }`}
                                >
                                    <t.icon className="w-3.5 h-3.5" />
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex-1 sm:max-w-xs">
                        <ThemedSelect
                            options={userOptions}
                            value={filterUserId}
                            onChange={setFilterUserId}
                            placeholder="Filtrer par recruteur"
                            size="sm"
                            dataTestid="completion-filter-user"
                        />
                    </div>
                </div>

                {completions.length === 0 ? (
                    <p className="text-sm text-white/40 italic text-center py-6" data-testid="completion-empty">
                        {tab === "active"
                            ? "Aucune validation active pour le moment."
                            : tab === "pending_review"
                              ? "Aucune preuve à valider pour le moment 🎉"
                              : "Aucune invalidation à afficher."}
                    </p>
                ) : (
                    <div className="space-y-2">
                        {completions.map((row) => (
                            <div
                                key={row.completion_id + (row.invalidated_at || "")}
                                className={`p-3 rounded-2xl border ${
                                    tab === "invalidated"
                                        ? "bg-red-500/[0.04] border-red-500/20"
                                        : "bg-white/[0.03] border-white/5"
                                }`}
                                data-testid={`completion-row-${row.completion_id}`}
                            >
                                <div className="flex items-center gap-3">
                                    {row.user_avatar ? (
                                        <img
                                            src={row.user_avatar}
                                            alt=""
                                            className="w-9 h-9 rounded-xl object-cover shrink-0"
                                        />
                                    ) : (
                                        <div className="w-9 h-9 rounded-xl bg-shariti-orange/20 flex items-center justify-center font-extrabold text-white shrink-0">
                                            {row.user_name?.[0]?.toUpperCase() || "?"}
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold text-white truncate flex items-center gap-1.5">
                                            {row.user_name}
                                            {tab === "invalidated" && (
                                                <span className="inline-flex items-center gap-0.5 text-[9px] font-extrabold uppercase tracking-widest bg-red-500/15 text-red-400 px-1.5 py-0.5 rounded-full">
                                                    <UserX className="w-2.5 h-2.5" />
                                                    Invalidée
                                                </span>
                                            )}
                                            <span className="text-white/40 text-xs font-light">
                                                · {row.user_email}
                                            </span>
                                        </p>
                                        <p className="text-[11px] text-white/55 truncate">
                                            {row.action_label}{" "}
                                            <span
                                                className={`font-bold ${
                                                    tab === "invalidated"
                                                        ? "text-red-400 line-through"
                                                        : tab === "pending_review"
                                                          ? "text-shariti-yellow"
                                                          : "text-shariti-orange-light"
                                                }`}
                                            >
                                                {tab === "pending_review"
                                                    ? `+${row.action_points || 0} pts à valider`
                                                    : `+${row.points_awarded} pts`}
                                            </span>{" "}
                                            · {tab === "pending_review"
                                                ? `envoyée le ${fmtDate(row.proof_uploaded_at)}`
                                                : `validé le ${fmtDate(row.completed_at)}`}
                                        </p>
                                    </div>
                                    {tab === "active" ? (
                                        <button
                                            type="button"
                                            onClick={() => invalidate(row)}
                                            data-testid={`invalidate-${row.completion_id}`}
                                            className="px-3 py-2 rounded-xl bg-white/5 hover:bg-red-500/20 text-xs font-bold text-white/70 hover:text-red-300 flex items-center gap-1.5 shrink-0"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                            Invalider
                                        </button>
                                    ) : tab === "pending_review" ? (
                                        <div className="flex gap-1.5 shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => approveProof(row)}
                                                data-testid={`approve-${row.completion_id}`}
                                                className="px-3 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-xs font-extrabold text-emerald-400 flex items-center gap-1.5"
                                            >
                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                                Valider
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => rejectProof(row)}
                                                data-testid={`reject-${row.completion_id}`}
                                                className="px-3 py-2 rounded-xl bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/40 text-xs font-bold text-white/70 hover:text-red-400 flex items-center gap-1.5"
                                            >
                                                <XCircle className="w-3.5 h-3.5" />
                                                Refuser
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="text-[10px] font-extrabold uppercase tracking-widest text-red-400 shrink-0">
                                            -{row.points_awarded} pts
                                        </span>
                                    )}
                                </div>
                                {/* iter-24 — proof preview using the stable signed endpoint */}
                                {tab === "pending_review" && (row.proof_image_url || row.proof_url) && (
                                    <a
                                        href={buildProofUrl(row.proof_image_url || row.proof_url)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`proof-${row.completion_id}`}
                                        className="mt-3 ml-12 block rounded-xl overflow-hidden border border-white/10 hover:border-shariti-orange/40 transition-all max-w-md group relative"
                                    >
                                        <img
                                            src={buildProofUrl(row.proof_image_url || row.proof_url)}
                                            alt="preuve"
                                            className="w-full max-h-64 object-contain bg-shariti-black"
                                            onError={(e) => {
                                                e.currentTarget.parentElement.innerHTML =
                                                    '<div class="p-4 text-xs text-white/40 italic">Preuve indisponible (ancien format)</div>';
                                            }}
                                        />
                                        <div className="absolute top-2 right-2 px-2 py-1 rounded-full bg-shariti-black/80 text-[10px] font-bold uppercase tracking-widest text-white/90 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                            <ExternalLink className="w-3 h-3" />
                                            Ouvrir
                                        </div>
                                    </a>
                                )}
                                {/* Audit trail (visible only on invalidated rows) */}
                                {tab === "invalidated" && (
                                    <div className="mt-2.5 ml-12 px-3 py-2 rounded-xl bg-black/20 border-l-2 border-red-500/40">
                                        <p className="text-[10px] font-thin tracking-[0.2em] uppercase text-red-400 mb-0.5">
                                            Audit
                                        </p>
                                        <p className="text-[11px] text-white/70">
                                            Invalidée par{" "}
                                            <span className="font-bold text-white">
                                                {row.invalidated_by_name || "—"}
                                            </span>
                                            {row.invalidated_by_email && (
                                                <span className="text-white/40">
                                                    {" "}({row.invalidated_by_email})
                                                </span>
                                            )}{" "}
                                            le{" "}
                                            <span className="font-bold text-white">
                                                {fmtDate(row.invalidated_at)}
                                            </span>
                                        </p>
                                        {row.invalidation_reason && (
                                            <p className="text-[11px] text-white/55 italic mt-1">
                                                « {row.invalidation_reason} »
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </motion.div>
        </div>
    );
}
